import React, { useEffect, useRef } from "react";
import { Animated, Easing, Pressable, StyleSheet, Text, View } from "react-native";
import { CF } from "../theme/turquoiseTheme.js";
import { getBottomInset, getTopInset } from "../utils/safeInsets.js";
import { useLanguage } from "../i18n/LanguageContext.js";
import { quoteAuthor, quoteReflection, quoteText } from "../content/dailyQuotes.js";

const TOP = getTopInset();
const BOTTOM = getBottomInset();

/**
 * Full-screen daily quote — shown after the welcome intro (and once per day after that).
 * @param {{ quote?: { id?: string, en?: string, fr?: string, text?: string, author?: string } | null, onContinue: () => void }} props
 */
export function DailyQuoteScreen({ quote, onContinue }) {
  const fade = useRef(new Animated.Value(0)).current;
  const rise = useRef(new Animated.Value(16)).current;
  const { t, locale } = useLanguage();
  const text = quote ? quoteText(quote, locale) || quote.text || "" : "";
  const author = quoteAuthor(quote);
  const reflection = quoteReflection(quote, locale);

  useEffect(() => {
    Animated.parallel([
      Animated.timing(fade, {
        toValue: 1,
        duration: 650,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
      Animated.timing(rise, {
        toValue: 0,
        duration: 650,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      }),
    ]).start();
  }, [fade, rise]);

  return (
    <View style={[styles.root, { paddingTop: TOP, paddingBottom: BOTTOM }]}>
      <View style={styles.glow} pointerEvents="none" />
      <Animated.View style={[styles.inner, { opacity: fade, transform: [{ translateY: rise }] }]}>
        <Text style={styles.brand}>Procsystim</Text>
        <Text style={styles.label}>{t("quote_label")}</Text>

        <View style={styles.card}>
          {text ? (
            <>
              <Text style={styles.quote}>“{text}”</Text>
              {author ? <Text style={styles.author}>— {author}</Text> : null}
              {reflection ? (
                <View style={styles.reflectionBox}>
                  <Text style={styles.reflectionLabel}>{t("quote_reflection_label")}</Text>
                  <Text style={styles.reflection}>{reflection}</Text>
                </View>
              ) : null}
            </>
          ) : (
            <Text style={styles.loading}>{t("quote_loading")}</Text>
          )}
        </View>

        <Pressable style={styles.cta} onPress={onContinue} accessibilityRole="button">
          <Text style={styles.ctaText}>{t("quote_continue")}</Text>
        </Pressable>
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: "#061018",
    justifyContent: "center",
  },
  glow: {
    position: "absolute",
    top: "20%",
    alignSelf: "center",
    width: 320,
    height: 320,
    borderRadius: 160,
    backgroundColor: "rgba(13, 148, 136, 0.18)",
  },
  inner: {
    paddingHorizontal: 24,
  },
  brand: {
    fontSize: 28,
    fontWeight: "900",
    color: CF.primaryBright,
    letterSpacing: -0.4,
    marginBottom: 8,
  },
  label: {
    fontSize: 13,
    fontWeight: "700",
    color: CF.textMuted,
    letterSpacing: 1.1,
    textTransform: "uppercase",
    marginBottom: 20,
  },
  card: {
    backgroundColor: "rgba(15, 26, 46, 0.92)",
    borderRadius: 18,
    borderWidth: 1,
    borderColor: CF.border,
    padding: 22,
    marginBottom: 28,
  },
  quote: {
    fontSize: 20,
    lineHeight: 30,
    color: CF.text,
    fontStyle: "italic",
    fontWeight: "500",
  },
  author: {
    marginTop: 14,
    fontSize: 14,
    fontWeight: "700",
    color: CF.primaryBright,
  },
  reflectionBox: {
    marginTop: 18,
    paddingTop: 14,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: CF.border,
  },
  reflectionLabel: {
    fontSize: 11,
    fontWeight: "800",
    letterSpacing: 1,
    textTransform: "uppercase",
    color: CF.textMuted,
    marginBottom: 8,
  },
  reflection: {
    fontSize: 15,
    lineHeight: 22,
    color: CF.text,
    fontWeight: "500",
  },
  loading: {
    fontSize: 15,
    color: CF.textMuted,
  },
  cta: {
    backgroundColor: CF.primaryDark,
    borderRadius: 14,
    paddingVertical: 16,
    alignItems: "center",
    borderWidth: 1,
    borderColor: CF.borderStrong,
  },
  ctaText: {
    color: CF.bgDeep,
    fontWeight: "900",
    fontSize: 16,
  },
});
